import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  WebsocketCmdTypes,
  WebsocketOpTypes,
} from '@qtek/core/websockets-core';
import { EntityMeta, Item, ItemStage } from '@qtek/shared/models';
import { pipe, Subject } from 'rxjs';
import { filter, mergeMap, takeUntil, tap } from 'rxjs/operators';

import { ItemService } from './agency.service';

export const AGENCY_STAGES = [ItemStage.CLOSED, ItemStage.AGENCY];

export interface AgencyState {
  allAgencies: Item[];
  agenciesMeta: EntityMeta;
}

export interface AgencyConnectPayload {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  q?: any[];
  view?: string;
  mysid?: string;
  op?: Extract<WebsocketOpTypes, 'sub' | 'fast_query'>;
  prms?: Record<string, string>;
  cmd?: WebsocketCmdTypes;
}

const initialState: AgencyState = {
  allAgencies: [] as Item[],
  agenciesMeta: new EntityMeta(),
};

export const AgencyStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    /** Fires the matching `connect` subscription's teardown by `mysid`. */
    _disconnect$: new Subject<string>(),
    /** Emits the created item when the WS `ins` echo arrives (replaces createItemSuccess). */
    itemCreated$: new Subject<{ res: Item; mysid: string }>(),
    /** Emits when the WS `ins` echo carries no result (replaces createItemFailure). */
    itemCreateFailed$: new Subject<unknown>(),
    /** Emits once a `query`/`fast_query` response is processed (success or failure). */
    queryCompleted$: new Subject<void>(),
  })),
  withMethods((store, service = inject(ItemService)) => ({
    connect: rxMethod<AgencyConnectPayload>(
      pipe(
        mergeMap(({ q, view, mysid, op, prms, cmd }) =>
          service.subscribe({ q, view, mysid, op, prms, cmd }).pipe(
            takeUntil(store._disconnect$.pipe(filter(id => id === mysid))),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            tap(({ res, op: responseOp, sts, mysid: resMysid }: any) => {
              switch (responseOp) {
                case 'query':
                case 'fast_query':
                  if (res) {
                    patchState(store, {
                      allAgencies: res || [],
                      agenciesMeta: sts?.meta,
                    });
                  }
                  store.queryCompleted$.next();
                  break;
                case 'ins':
                  if (res) {
                    if (AGENCY_STAGES.includes(res.stage)) {
                      patchState(store, state => ({
                        allAgencies: [res, ...state.allAgencies],
                      }));
                    }
                    store.itemCreated$.next({ res, mysid: resMysid ?? mysid });
                  } else {
                    store.itemCreateFailed$.next(sts);
                  }
                  break;
                case 'upd':
                  if (res) {
                    patchState(store, state => {
                      const allAgencies = state.allAgencies.slice();
                      const index = allAgencies.findIndex(
                        element => element.sequenceNumber === res.sequenceNumber
                      );
                      if (index !== -1) {
                        allAgencies[index] = res;
                      } else {
                        // if the item is not present add it to the collection
                        allAgencies.unshift(res);
                      }
                      return { allAgencies };
                    });
                  }
                  break;
                case 'del':
                  if (res) {
                    patchState(store, state => ({
                      allAgencies: state.allAgencies.filter(
                        agency => agency.sequenceNumber !== res.sequenceNumber
                      ),
                    }));
                  }
                  break;
                default:
                  break;
              }
            })
          )
        )
      )
    ),

    getItems(payload?: unknown): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      service.getItems(payload as any);
    },

    getItem(id: string): void {
      service.getItemById({ id });
    },

    createItem(item: Item): void {
      service.createItem({ res: item });
    },

    updateItem(item: Item): void {
      service.updateItem({ res: item, id: item.sequenceNumber });
    },

    deleteItem(id: string): void {
      service.deleteItem({ id });
    },

    disconnect(mysid: string): void {
      store._disconnect$.next(mysid);
      patchState(store, {
        allAgencies: [] as Item[],
        agenciesMeta: new EntityMeta(),
      });
    },
  }))
);
